import type { AuthRepository } from "@cmts/auth/authRepository.js";
import type { CmtsRepositorySuite } from "@cmts/repositories/cmtsRepositorySuite.js";

export type CmtsReduxThunkExtra = {
  readonly authRepository?: AuthRepository | undefined;
  readonly repositories?: CmtsRepositorySuite | undefined;
};

export type CmtsSerializableError = {
  readonly code?: string | undefined;
  readonly status?: number | undefined;
  readonly message: string;
  readonly correlationId?: string | undefined;
  readonly retryable?: boolean | undefined;
  readonly atUtc: string;
};

export function serializeReduxError(error: unknown, fallbackMessage: string): CmtsSerializableError {
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    const envelope = typeof record.envelope === "object" && record.envelope !== null ? record.envelope as Record<string, unknown> : record;
    const message = typeof envelope.message === "string" && envelope.message.trim() ? envelope.message : fallbackMessage;
    const code = typeof envelope.code === "string" ? envelope.code : undefined;
    const status = typeof envelope.status === "number" ? envelope.status : undefined;
    const correlationId = typeof envelope.correlationId === "string" ? envelope.correlationId : undefined;
    const retryable = typeof envelope.retryable === "boolean" ? envelope.retryable : undefined;
    return { code, status, message, correlationId, retryable, atUtc: new Date().toISOString() };
  }

  return { message: error instanceof Error && error.message ? error.message : fallbackMessage, atUtc: new Date().toISOString() };
}


export function serializeRepositoryFailure(diagnostics: readonly unknown[], fallbackMessage: string): CmtsSerializableError {
  const first = diagnostics.find((item) => typeof item === "object" && item !== null) as Record<string, unknown> | undefined;
  return {
    code: typeof first?.code === "string" ? first.code : undefined,
    status: typeof first?.status === "number" ? first.status : undefined,
    message: typeof first?.message === "string" && first.message.trim() ? first.message : fallbackMessage,
    correlationId: typeof first?.correlationId === "string" ? first.correlationId : undefined,
    retryable: typeof first?.retryable === "boolean" ? first.retryable : undefined,
    atUtc: new Date().toISOString(),
  };
}

export function readReduxRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function readReduxString(value: unknown, keys: readonly string[], fallback = ""): string {
  const record = readReduxRecord(value);
  for (const key of keys) {
    const candidate = record[key];
    if (typeof candidate === "string" && candidate.trim()) return candidate;
    if (typeof candidate === "number" || typeof candidate === "boolean") return String(candidate);
  }
  return fallback;
}
